import { CompanyGroupIdentifierSystem } from "@/api-schema-enums";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Fingerprint } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CompanyGroupIdentifierFormValues, isDuplicateCompanyGroupIdentifier } from "../company-group-identifiers";

interface CompanyGroupFormWithIdentifiers {
  identifiers: CompanyGroupIdentifierFormValues;
}

interface CompanyGroupIdentifierFormCardProps {
  duplicateKeys?: ReadonlySet<string>;
  isCheckingDuplicates?: boolean;
}

const fixedIdentifierFields = [
  {
    name: "identifiers.paperless",
    type: CompanyGroupIdentifierSystem.PAPERLESS,
    labelKey: "masterdata:PaperlessWorkspaceId",
    placeholderKey: "masterdata:PaperlessWorkspaceIdExample",
  },
] as const;

export function CompanyGroupIdentifierFormCard({
  duplicateKeys = new Set(),
  isCheckingDuplicates = false,
}: CompanyGroupIdentifierFormCardProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const { control } = useFormContext<CompanyGroupFormWithIdentifiers>();

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="h-4 w-4" />
          {t("masterdata:CompanyGroupIdentifiers")}
        </CardTitle>
        <CardDescription>{t("masterdata:CompanyGroupIdentifiersDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-5">
          {fixedIdentifierFields.map((identifierField) => (
            <FormField
              key={identifierField.type}
              control={control}
              name={identifierField.name}
              render={({ field }) => {
                const hasDuplicate = isDuplicateCompanyGroupIdentifier(
                  duplicateKeys,
                  identifierField.type,
                  field.value,
                );

                return (
                  <FormItem>
                    <FormLabel>{t(identifierField.labelKey)}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t(identifierField.placeholderKey)}
                        inputMode="numeric"
                        error={hasDuplicate}
                      />
                    </FormControl>
                    <FormMessage />
                    {hasDuplicate ? (
                      <p className="text-error text-sm">{t("masterdata:CompanyGroupIdentifierAlreadyExists")}</p>
                    ) : null}
                  </FormItem>
                );
              }}
            />
          ))}
        </div>

        {isCheckingDuplicates ? (
          <p className="text-muted-foreground text-sm">{t("masterdata:CheckingDuplicateCompanyGroupIdentifiers")}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
